
import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AnimateInView from '@/components/ui/animate-in-view';
import GoldSectionDivider from '@/components/ui/gold-section-divider';

// Installation process steps data
const installationSteps = [
  {
    step: 1,
    title: "Assessment",
    description: "Audit your fence's condition and potential weak points."
  },
  {
    step: 2,
    title: "Cable Integration",
    description: "Install our ultra-thin cable along fence lines, blending seamlessly with metal or wooden structures."
  },
  {
    step: 3,
    title: "Smart Calibration",
    description: "Tailor detection thresholds by fence type (rustic, chainlink, iron)."
  },
  {
    step: 4,
    title: "Comprehensive Overview",
    description: "Provide guidance on how the system works and connect with monitoring platform for in depth usage details."
  },
  {
    step: 5,
    title: "Expansion Ready",
    description: "Add motion sensors or cameras for layered defense."
  }
];

// Fence types data
const fenceTypes = [
  {
    id: 'retrofit',
    label: 'Retrofit',
    title: 'Retrofit Installation',
    description: 'Enhance existing fences and barriers with advanced detection technology.',
    features: [
      'Non-invasive installation preserves existing structures', 
      'Compatible with most fence types and materials', 
      'No visible changes to the fence appearance', 
      'Quick deployment with minimal disruption', 
      'Scalable to cover any perimeter length'
    ],
    image: '/images/Solutions-Fence-Overview-Section4.png'
  },
  {
    id: 'new-build',
    label: 'New Build',
    title: 'New Construction Integration',
    description: 'Seamlessly incorporate detection systems during construction phases.',
    features: [
      'Invisible integration within the fence structure', 
      'Designed in conjunction with architectural elements', 
      'Enhanced detection capabilities and system redundancy', 
      'Optimal positioning for maximum effectiveness', 
      'Future-ready with upgrade pathways built in'
    ],
    image: '/images/Solutions-Fence-New-Build-3.png'
  }
];

const FenceInstallation = () => {
  const [activeTab, setActiveTab] = useState('retrofit');
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);

  return (
    <section className="section bg-boundri-background" aria-labelledby="installation-types-heading">
      <div className="container mx-auto">
        <div className="flex flex-col items-center mb-8">
          <GoldSectionDivider centered />
          <h2 id="installation-types-heading" className="text-center text-boundri-DEFAULT mb-2">
            How We Install It
          </h2>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <ol className="relative border-l border-boundri-DEFAULT/30 ml-6 mb-8">
            {installationSteps.map((item, index) => (
              <AnimateInView key={index} delay={150 * index} animation="slide-up">
                <li className="mb-10 ml-8 last:mb-0">
                  <span className="absolute flex items-center justify-center w-8 h-8 bg-boundri-DEFAULT rounded-full -left-4 text-white">
                    {item.step}
                  </span>
                  <h3 className="text-xl font-serif text-boundri-DEFAULT mb-2">{item.title}</h3>
                  <p className="text-boundri-charcoal">{item.description}</p>
                </li>
              </AnimateInView>
            ))}
          </ol>
        </div>
        
        <div className="max-w-4xl mx-auto mt-16">
          <AnimateInView>
            <Tabs defaultValue="retrofit" value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                {fenceTypes.map(type => (
                  <TabsTrigger key={type.id} value={type.id}>
                    {type.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {fenceTypes.map(type => (
                <TabsContent key={type.id} value={type.id} className="mt-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                    <div>
                      <h3 className="text-2xl font-serif text-boundri-DEFAULT mb-4">
                        {type.title}
                      </h3>
                      <p className="mb-6 text-boundri-charcoal">
                        {type.description}
                      </p>
                      <ul className="space-y-3">
                        {type.features.map((feature, index) => (
                          <li 
                            key={index} 
                            className={`flex items-start p-2 rounded-md transition-colors duration-200 ${hoveredFeature === index ? 'bg-boundri-background' : ''}`} 
                            onMouseEnter={() => setHoveredFeature(index)} 
                            onMouseLeave={() => setHoveredFeature(null)}
                          >
                            <CheckCircle2 className="text-boundri-teal mr-2 h-5 w-5 shrink-0" />
                            <span className="text-boundri-charcoal">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-white rounded-lg overflow-hidden shadow-lg">
                      <img src={type.image} alt={type.title} className="w-full h-full object-cover aspect-video" loading="lazy" />
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </AnimateInView>
        </div>
      </div>
    </section>
  );
};

export default FenceInstallation;
